/**
 * Explosion-related particle effects extracted from ParticleSystem.
 * Each function takes a ParticleCore as its first parameter.
 */

import { CRATER, STRUCTURAL } from '../../constants';
import { configParticle, type ParticleCore } from './types';
import { darken, lighten, warm, cool, generatePalette } from './color-utils';

/** Legacy explosion — used by death sequence and other non-weapon effects */
export function spawnExplosion(core: ParticleCore, x: number, y: number, color: number, count: number): void {
  for (let i = 0; i < count; i++) {
    core.spawnParticle(
      x, y, color,
      3, 15,
      0.3, 0.8,
      0.5, 2.0,
      0.08 + Math.random() * 0.08,
      0.98,
    );
  }
}

/**
 * Terrain debris — multi-layer Sakurai-style terrain destruction effect.
 *
 * Layer 1 (immediate): Heavy chunks — dark terrain pieces, slow arcing, high gravity
 * Layer 2 (immediate): Shrapnel spray — bright accent streaks, velocity-aligned, fast
 * Layer 3 (+60ms):     Dirt cloud — soft brown dust rising from crater, smoke baton pass
 * Layer 4 (immediate): Impact lines — crisp radial shrapnel lines (straight vs curves contrast)
 * Layer 5 (immediate): Dust shockwave — earth-toned ring at ground level
 */
export function spawnTerrainDebris(core: ParticleCore, cx: number, cy: number, blastRadius: number, particleScale: number): void {
  const baseCount = Math.floor(blastRadius * CRATER.DEBRIS_COUNT_MULT * particleScale);

  // Sakurai: "mix bright and dark" — dark terrain + bright surface accents
  const darkColors = [0x0a0c1e, 0x0c0e28, 0x080818, 0x060614];
  const brightColors = [0x1a1e40, 0x222850, 0x2a3060, 0x182848]; // surface terrain blues
  const accentColors = [0x445588, 0x334466, 0x556699]; // bright accents for any-background visibility

  // === Layer 1: Heavy chunks (60% of count) — slow, arcing, heavy gravity ===
  const chunkCount = Math.ceil(baseCount * 0.6);
  for (let i = 0; i < chunkCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const rimDist = blastRadius * (0.65 + Math.random() * 0.35);
    const spawnX = cx + Math.cos(angle) * rimDist;
    const spawnY = cy + Math.sin(angle) * rimDist;
    const outAngle = angle + (Math.random() - 0.5) * 0.5;
    const speed = 2 + Math.random() * 4;

    // Sakurai: mix dark and bright — mostly dark, some bright surface pieces
    const r = Math.random();
    const color = r < 0.55 ? darkColors[Math.floor(Math.random() * darkColors.length)]
      : r < 0.85 ? brightColors[Math.floor(Math.random() * brightColors.length)]
        : accentColors[Math.floor(Math.random() * accentColors.length)];

    const p = core.getInactive();
    if (!p) return;

    configParticle(p, core, {
      x: spawnX,
      y: spawnY,
      vx: Math.cos(outAngle) * speed,
      vy: Math.sin(outAngle) * speed - 2 - Math.random() * 3, // upward bias
      color,
      life: 0.4 + Math.random() * 0.5,
      scale: 0.3 + Math.random() * 0.5,
      alpha: 0.8 + Math.random() * 0.2,
      gravity: 0.15 + Math.random() * 0.05, // heavy — these are dirt
      friction: 0.97,
      rotationSpeed: (Math.random() - 0.5) * 10,
      addBlend: false,
    });
  }

  // === Layer 2: Shrapnel spray (25% of count) — fast, stretched, bright ===
  // Sakurai: velocity-aligned streaks + bright accents for "visible on any background"
  const sprayCount = Math.ceil(baseCount * 0.25);
  for (let i = 0; i < sprayCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const rimDist = blastRadius * (0.8 + Math.random() * 0.2);
    const outAngle = angle + (Math.random() - 0.5) * 0.3;
    const speed = 6 + Math.random() * 8;

    const r = Math.random();
    const color = r < 0.4 ? accentColors[Math.floor(Math.random() * accentColors.length)]
      : r < 0.7 ? brightColors[Math.floor(Math.random() * brightColors.length)]
        : 0x667799; // bright blue-grey shrapnel

    core.spawnParticleAtAngle(
      cx + Math.cos(angle) * rimDist,
      cy + Math.sin(angle) * rimDist,
      outAngle, speed,
      color,
      0.12 + Math.random() * 0.2,   // brief — Sakurai: shorten lifespans
      0.15 + Math.random() * 0.2,
      0.08 + Math.random() * 0.06,
      0.95,
      false, true,                    // NORMAL + hard — crisp shrapnel
      0.75,
      2.5 + Math.random() * 2,       // stretch 2.5-4.5x — velocity-aligned streaks
    );
  }

  // === Layer 3: Dirt cloud (+60ms) — Sakurai smoke baton pass for terrain ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 60,
    callback: () => {
      const dustCount = Math.ceil(baseCount * 0.3);
      for (let i = 0; i < dustCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = blastRadius * (0.3 + Math.random() * 0.5);
        const dustColor = Math.random() < 0.4 ? 0x1a1520
          : Math.random() < 0.6 ? 0x221a28
            : 0x181422;
        core.spawnParticle(
          cx + Math.cos(angle) * dist,
          cy + Math.sin(angle) * dist,
          dustColor,
          0.5, 2.5,
          0.5, 1.2,       // longer life — dust hangs
          1.2, 2.5,       // large soft clouds
          -0.015,          // float up — rising dust
          0.985,
          false, false,    // NORMAL + soft — dark dust cloud
          0.45,
        );
      }
    },
  });

  // === Layer 4: Shrapnel impact lines — Sakurai "straight lines vs curves" ===
  // Crisp radial lines from crater, earth-toned — contrasts against soft particle cloud
  const lineCount = 6 + Math.floor(Math.random() * 3);
  for (let i = 0; i < lineCount; i++) {
    const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const dark = Math.random() < 0.3;
    const life = 0.1 + Math.random() * 0.08;
    core.impactLines.push({
      x: cx, y: cy, angle,
      innerRadius: blastRadius * 0.6 + Math.random() * 10,
      outerRadius: blastRadius * 0.8 + Math.random() * 15,
      expandSpeed: 120 + Math.random() * 80,
      color: dark ? 0x0a0c1e : (Math.random() < 0.5 ? 0x334466 : 0x556688),
      lineWidth: dark ? 3 : 2 + Math.random(),
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // === Layer 5: Dust ring shockwave — ground-level expanding ring ===
  core.spawnShockwave(cx, cy, 0x221a30, blastRadius * 0.9, 180, 2.5, 0.2);
}

/**
 * Rich multi-layer explosion — atom bomb effect.
 * Phase 1 (immediate): Initial combustion — tight orange/yellow core that gathers.
 * Phase 2 (delayed ~120ms): Main outward blast — cyan cloud, gems, streaks, shockwaves.
 */
export function spawnRichExplosion(core: ParticleCore, x: number, y: number, color: number, blastRadius: number, scale: number): void {
  const baseCount = Math.floor(blastRadius * scale);
  const palette = generatePalette(color);

  // === PHASE 1: Initial combustion (immediate) ===
  // Ultra-tight atom bomb core — barely moves, dense and hot

  // Inner glow — ADD, soft circular, very dark, nearly stationary
  const innerCount = Math.max(30, Math.floor(baseCount * 0.35));
  for (let i = 0; i < innerCount; i++) {
    const innerColor = Math.random() < 0.35 ? darken(color, 0.85) : (Math.random() < 0.5 ? darken(warm(color, 0.3), 0.8) : darken(cool(color, 0.3), 0.8));
    core.spawnParticle(
      x, y, innerColor,
      0.1, 1,
      0.5, 1.0,
      3.5, 6.0,
      0, 0.6,
      true, false,
      0.9,
    );
  }

  // Outer core — NORMAL, soft circular, orange + cyan, ultra tight
  const coreCount = Math.max(50, Math.floor(baseCount * 0.5));
  for (let i = 0; i < coreCount; i++) {
    const r = Math.random();
    const coreColor = r < 0.25 ? color
      : r < 0.5 ? darken(color, 0.4)
        : r < 0.7 ? warm(color, 0.4)
          : r < 0.85 ? darken(warm(color, 0.3), 0.2)
            : cool(color, 0.4);
    core.spawnParticle(
      x, y, coreColor,
      0.2, 2,
      0.5, 1.0,
      2.0, 4.5,
      0, 0.6,
      false, false,
      0.85,
    );
  }

  // Tight combustion shockwaves
  core.spawnShockwave(x, y, darken(color, 0.4), blastRadius * 0.4, 160, 3, 0.18);
  core.spawnShockwave(x, y, cool(color, 0.4), blastRadius * 0.3, 120, 2, 0.15);

  // === PHASE 2: Main outward blast (delayed ~90ms) ===

  core.pendingBlasts.push({
    timestamp: Date.now() + 90,
    callback: () => {
      // === SAKURAI: Initial flash — bright + long relative to explosion ===
      // "A flash of light occurs, instantly flashing brightly"
      // "Mix in plenty of black so it's visible against white backgrounds"
      for (let i = 0; i < 20; i++) {
        const r = Math.random();
        const flashColor = r < 0.3 ? 0x000000   // black mixed in (Sakurai)
          : r < 0.55 ? lighten(color, 0.6)
            : r < 0.75 ? 0xffffff
              : color;
        core.spawnParticle(
          x, y, flashColor,
          0, 1,
          0.15, 0.3,       // longer than before — "duration of flash is quite long"
          3.5, 7.0,        // large
          0, 0.4,
          flashColor === 0x000000 ? false : true,  // black = NORMAL, others = ADD
          false,
          flashColor === 0x000000 ? 0.7 : 0.9,
        );
      }

      // Dense cyan cloud — Sakurai: offset spawns to reduce center waste
      // Micro-stagger: split into 3 sub-waves 10ms apart
      const cloudCount = Math.max(160, Math.floor(baseCount * 2.2));
      const cloudPerWave = Math.ceil(cloudCount / 3);
      for (let w = 0; w < 3; w++) {
        const waveSpawn = () => {
          for (let i = 0; i < cloudPerWave; i++) {
            const useCircle = Math.random() < 0.3;
            const cloudColor = Math.random() < 0.85 ? color : palette[Math.floor(Math.random() * palette.length)];
            core.spawnParticle(
              x, y, cloudColor,
              1, 8,
              0.25, 0.8,       // shorter lifespan
              0.5, 1.5,        // slightly smaller
              0.04 + Math.random() * 0.08,
              0.88,
              true, !useCircle,
              0.8,
              1,
              8,               // spawn offset — reduce center overlap
            );
          }
        };
        if (w === 0) waveSpawn();
        else core.pendingBlasts.push({ timestamp: Date.now() + 90 + w * 10, callback: waveSpawn });
      }

      // Inner hot ring — orange particles in a tight circle
      const hotRingCount = 28;
      for (let i = 0; i < hotRingCount; i++) {
        const angle = (i / hotRingCount) * Math.PI * 2;
        const hotColor = Math.random() < 0.5 ? warm(color, 0.4) : darken(color, 0.3);
        core.spawnParticleAtAngle(
          x, y, angle,
          8 + Math.random() * 4,
          hotColor,
          0.3 + Math.random() * 0.2,
          0.6 + Math.random() * 0.4,
          0, 0.92,
          false, false,  // NORMAL, soft — warm ring
          0.8,
        );
      }

      // Outer ring burst — cyan crown, evenly spaced
      const ringCount = 36;
      for (let i = 0; i < ringCount; i++) {
        const angle = (i / ringCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.1;
        const ringColor = Math.random() < 0.7 ? color : lighten(color, 0.4);
        core.spawnParticleAtAngle(
          x, y, angle,
          14 + Math.random() * 6,   // tighter
          ringColor,
          0.25 + Math.random() * 0.15,
          0.4 + Math.random() * 0.3,
          0, 0.94,
          true, true,
          0.85,
        );
      }

      // Gem haze — NORMAL blend for Sakurai contrast against ADD cloud
      const hazeCount = Math.max(30, Math.floor(baseCount * 0.25));
      for (let i = 0; i < hazeCount; i++) {
        const hazeColor = palette[Math.floor(Math.random() * palette.length)];
        core.spawnParticle(
          x, y, hazeColor,
          4, 12,
          0.5, 1.1,
          1.5, 3.0,
          0.15 + Math.random() * 0.1,
          0.95,
          false, false,    // NORMAL blend — crisp color, not washed out
          0.3,
          1,
          8,
        );
      }

      // Chiclet gems — smaller, denser, spawn-offset, 25% use cluster billboard
      // ~40% get tangential velocity for spiral corkscrew motion
      const gemCount = Math.max(95, Math.floor(baseCount * 0.8));
      for (let i = 0; i < gemCount; i++) {
        const gemColor = palette[Math.floor(Math.random() * palette.length)];
        const useCluster = Math.random() < 0.25; // Sakurai: multi-gem billboard
        const useCircle = !useCluster && Math.random() < 0.3;
        const spiral = Math.random() < 0.4;
        const angle = Math.random() * Math.PI * 2;
        const speed = 5 + Math.random() * 9;
        const tangent = spiral ? (Math.random() < 0.5 ? 1 : -1) * (3 + Math.random() * 5) : 0;

        const p = core.getInactive();
        if (!p) continue;

        // Sakurai: offset spawn along travel direction to reduce center waste
        const offset = Math.random() * 10;
        const hard = !useCircle && !useCluster;

        configParticle(p, core, {
          x: x + Math.cos(angle) * offset,
          y: y + Math.sin(angle) * offset,
          vx: Math.cos(angle) * speed + Math.cos(angle + Math.PI / 2) * tangent,
          vy: Math.sin(angle) * speed + Math.sin(angle + Math.PI / 2) * tangent,
          color: gemColor,
          life: 0.5 + Math.random() * 0.7,  // shorter lifespan
          scale: useCluster ? 0.6 + Math.random() * 0.5 : 0.3 + Math.random() * 0.5, // smaller singles
          alpha: 0.5 + Math.random() * 0.3,
          gravity: 0.2 + Math.random() * 0.15,
          friction: 0.96,
          rotationSpeed: spiral ? (Math.random() - 0.5) * 10 : (Math.random() - 0.5) * 6,
          hard,
        });

        if (useCluster) p.sprite.texture = core.clusterTexture;
      }

      // Radial streaks — velocity-aligned, spawn offset
      const streakCount = Math.max(50, Math.floor(baseCount * 0.6));
      for (let i = 0; i < streakCount; i++) {
        core.spawnParticle(
          x, y, color,
          12, 25,
          0.15, 0.3,
          0.12, 0.3,
          0.08 + Math.random() * 0.1,
          0.96,
          true, true,
          0.8,
          3 + Math.random() * 2,  // stretch 3-5x — actually read as streaks
          6,
        );
      }

      // Light rays — god rays, tighter
      const rayCount = 14;
      for (let i = 0; i < rayCount; i++) {
        core.spawnParticle(
          x, y, lighten(color, 0.5),
          25, 42,        // tighter
          0.12, 0.25,    // brief
          0.08, 0.15,
          0,
          0.99,
          true, true,
          0.55,
          10,
        );
      }

      // === SAKURAI: Smoke is DELAYED — "baton pass" from flames to smoke ===
      // "Like a baton pass, increasing the dominance of the smoke"
      // Smoke spawns ~200ms after main blast, taking over as flames recede
      core.pendingBlasts.push({
        timestamp: Date.now() + 90 + 200,
        callback: () => {
          const smokeCount = Math.max(45, Math.floor(baseCount * 0.4));
          for (let i = 0; i < smokeCount; i++) {
            const r = Math.random();
            const smokeColor = r < 0.2 ? 0x332211
              : r < 0.4 ? 0x443322
                : r < 0.55 ? 0x554433
                  : r < 0.75 ? 0x333344
                    : 0x444466;
            core.spawnParticle(
              x, y, smokeColor,
              0.5, 3,
              0.8, 2.0,
              1.8, 3.5,
              -0.012,
              0.985,
              false, false,
              0.5,
              1,
              5,
            );
          }
        },
      });

      // Sparks — smaller, more, spawn offset
      const sparkCount = Math.max(50, Math.floor(baseCount * 0.35));
      for (let i = 0; i < sparkCount; i++) {
        const sparkColor = Math.random() < 0.7 ? color : palette[Math.floor(Math.random() * palette.length)];
        const useCircle = Math.random() < 0.4;
        core.spawnParticle(
          x, y, sparkColor,
          8, 20,
          0.2, 0.45,      // shorter
          0.08, 0.25,      // smaller
          0.3,
          0.95,
          true, !useCircle,
          0.85,
          1,
          5,               // spawn offset
        );
      }

      // Seekers — velocity-aligned, tighter
      const seekerCount = Math.max(6, Math.floor(baseCount * 0.06));
      for (let i = 0; i < seekerCount; i++) {
        const seekerColor = Math.random() < 0.6 ? color : lighten(color, 0.4);
        core.spawnParticle(
          x, y, seekerColor,
          16, 28,        // punchy outward
          0.12, 0.25,    // shorter life — fade out faster
          0.18, 0.35,
          0,             // no gravity — pure radial
          0.97,
          true, true,
          0.85,
          6,
        );
      }

      // Debris — shorter arcs, spawn offset
      const debrisCount = Math.max(35, Math.floor(baseCount * 0.28));
      for (let i = 0; i < debrisCount; i++) {
        const debrisColor = Math.random() < 0.6 ? color : palette[Math.floor(Math.random() * palette.length)];
        core.spawnParticle(
          x, y, debrisColor,
          6, 14,
          0.35, 0.9,      // shorter life
          0.2, 0.45,      // smaller
          0.45 + Math.random() * 0.2,
          0.98,
          false, true,
          0.75,
          1,
          8,               // spawn offset
        );
      }

      // Micro-particles — even tinier, more, offset to fill gaps
      const microCount = Math.max(75, Math.floor(baseCount * 0.55));
      for (let i = 0; i < microCount; i++) {
        const microColor = Math.random() < 0.7 ? color : palette[Math.floor(Math.random() * palette.length)];
        core.spawnParticle(
          x, y, microColor,
          1, 6,
          0.15, 0.45,     // shorter
          0.1, 0.3,       // smaller
          0.03 + Math.random() * 0.05,
          0.90,
          true, Math.random() < 0.5,
          0.65,
          1,
          6,               // spawn offset
        );
      }

      // Embers — round hot particles floating up
      const emberCount = Math.max(22, Math.floor(baseCount * 0.175));
      for (let i = 0; i < emberCount; i++) {
        const emberColor = Math.random() < 0.4 ? warm(color, 0.4)
          : Math.random() < 0.5 ? color
            : Math.random() < 0.5 ? lighten(color, 0.4)
              : cool(color, 0.3);    // cool embers too
        core.spawnParticle(
          x, y, emberColor,
          1.5, 6,        // tighter
          1.5, 2.5,
          0.25, 0.7,
          -0.04 - Math.random() * 0.03,
          0.99,
          true, false,
          0.95,
        );
      }

      // Afterglow — lingering bright center
      for (let i = 0; i < 12; i++) {
        const glowColor = Math.random() < 0.5 ? color : cool(color, 0.2);
        core.spawnParticle(
          x, y, glowColor,
          0, 0.3,
          0.8, 1.8,
          2.5, 4.5,
          0, 0.5,
          true, false,
          0.35,
        );
      }

      // Shockwave rings — layered, tighter
      core.spawnShockwave(x, y, color, blastRadius * 1.4, 320, 6, 0.35);
      core.spawnShockwave(x, y, warm(color, 0.4), blastRadius * 1.0, 260, 4, 0.3);
      core.spawnShockwave(x, y, color, blastRadius * 0.6, 400, 2, 0.12);
      core.spawnShockwave(x, y, cool(color, 0.2), blastRadius * 1.6, 180, 3, 0.45);

      // === SAKURAI CONTRAST: Impact lines — crisp straight lines, not ADD ===
      // Thicker, longer life, mix bright + dark for visibility on any background
      const lineCount = 14;
      for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
        const dark = Math.random() < 0.3; // 30% dark lines for contrast
        const life = 0.18 + Math.random() * 0.12;
        core.impactLines.push({
          x, y, angle,
          innerRadius: 3 + Math.random() * 8,
          outerRadius: 20 + Math.random() * 20,
          expandSpeed: 200 + Math.random() * 140,
          color: dark ? darken(color, 0.8) : (Math.random() < 0.5 ? 0xffffff : color),
          lineWidth: dark ? 4 + Math.random() * 2 : 3 + Math.random() * 2,
          life,
          maxLife: life,
          freezeImmune: false,
        });
      }

      // === SAKURAI CONTRAST: Dark shockwave outlines — shadow ring trailing bright ones ===
      core.spawnShockwave(x, y, darken(color, 0.85), blastRadius * 1.5, 300, 4, 0.3);
      core.spawnShockwave(x, y, darken(color, 0.85), blastRadius * 1.1, 240, 3, 0.25);

      // === SAKURAI CONTRAST: Silhouette flash — dark circle contracts at center ===
      // Brief dark "hole" that the bright explosion fills in — depth contrast
      for (let i = 0; i < 8; i++) {
        core.spawnParticle(
          x, y, 0x000a11,
          0, 0.5,
          0.1, 0.2,        // very brief
          3.0, 5.0,        // large — covers center
          0, 0.4,
          false, false,     // NORMAL, soft
          0.7,
        );
      }

      // === SAKURAI CONTRAST: Dark contrast particles — frame the bright explosion ===
      // "Mixing in darker elements is effective — creates contrast"
      const darkCount = 20;
      for (let i = 0; i < darkCount; i++) {
        const darkColor = Math.random() < 0.3 ? darken(color, 0.92) : (Math.random() < 0.5 ? darken(color, 0.87) : 0x111111);
        core.spawnParticle(
          x, y, darkColor,
          3, 10,
          0.3, 0.7,
          1.0, 2.5,
          0.1 + Math.random() * 0.1,
          0.92,
          false, false,   // NORMAL blend, soft — dark shadow framing
          0.6,
          1,
          12,              // larger offset — sits behind the bright stuff
        );
      }

      // === SAKURAI CONTRAST: Anime hit cross — crisp flat white star at impact ===
      // "Clearly finished effects using polygons or flat colors as the main focus"
      // 4 lines forming a cross, brief and bright
      for (let i = 0; i < 4; i++) {
        const crossAngle = (i / 4) * Math.PI * 2 + Math.PI / 4; // 45° offset
        core.impactLines.push({
          x, y, angle: crossAngle,
          innerRadius: 0,
          outerRadius: blastRadius * 0.5,
          expandSpeed: 0, // static — doesn't expand, just fades
          color: 0xccddee,
          lineWidth: 4,
          life: 0.08,
          maxLife: 0.08,
          freezeImmune: false,
        });
      }
    },
  });

  // === PHASE 3: Sparkle twinkles (delayed ~225-525ms) ===
  // Tiny bright pops at random positions within blast radius — gems catching light
  const twinkleCount = 18;
  for (let i = 0; i < twinkleCount; i++) {
    const delay = 225 + Math.random() * 300;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const ang = Math.random() * Math.PI * 2;
        const dist = Math.random() * blastRadius * 0.8;
        const tx = x + Math.cos(ang) * dist;
        const ty = y + Math.sin(ang) * dist;
        const twinkleColor = Math.random() < 0.3 ? 0xffffff : palette[Math.floor(Math.random() * palette.length)];
        core.spawnParticle(
          tx, ty, twinkleColor,
          0, 0.2,
          0.05, 0.12,    // very brief flash
          0.3, 0.8,
          0, 0.5,
          true, Math.random() < 0.5,
          0.95,
        );
      },
    });
  }

  // === PHASE 3b: Secondary micro-bursts (delayed ~150-375ms) ===
  // 3 small chain detonations at random offsets — makes explosion feel alive
  const burstCount = 3;
  for (let i = 0; i < burstCount; i++) {
    const delay = 150 + Math.random() * 225;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const ang = Math.random() * Math.PI * 2;
        const dist = blastRadius * (0.3 + Math.random() * 0.5);
        const bx = x + Math.cos(ang) * dist;
        const by = y + Math.sin(ang) * dist;

        // Small particle cluster
        for (let j = 0; j < 12; j++) {
          const burstColor = Math.random() < 0.5 ? color : palette[Math.floor(Math.random() * palette.length)];
          core.spawnParticle(
            bx, by, burstColor,
            2, 8,
            0.15, 0.35,
            0.3, 0.7,
            0.1 + Math.random() * 0.1,
            0.92,
            true, Math.random() < 0.6,
            0.8,
          );
        }

        // Tiny shockwave per burst
        core.spawnShockwave(bx, by, color, blastRadius * 0.3, 200, 2, 0.15);
      },
    });
  }

  // === PHASE 4: Gem rain (delayed ~190-750ms) ===
  // Gems spawning around the blast in all directions, drifting outward with gravity
  const rainCount = 70;
  for (let i = 0; i < rainCount; i++) {
    const delay = 190 + Math.random() * 560;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const rainColor = palette[Math.floor(Math.random() * palette.length)];
        // Spawn in a ring around the explosion at varying distances
        const ang = Math.random() * Math.PI * 2;
        const dist = blastRadius * (0.4 + Math.random() * 0.8);
        const rx = x + Math.cos(ang) * dist;
        const ry = y + Math.sin(ang) * dist;
        // Drift outward from center + gravity pulls down
        const outSpeed = 1 + Math.random() * 3;
        const p = core.getInactive();
        if (!p) return;

        const hard = Math.random() < 0.6;

        configParticle(p, core, {
          x: rx,
          y: ry,
          vx: Math.cos(ang) * outSpeed + (Math.random() - 0.5) * 2,
          vy: Math.sin(ang) * outSpeed + (Math.random() - 0.5) * 2,
          color: rainColor,
          life: 0.8 + Math.random() * 1.0,
          scale: 0.4 + Math.random() * 0.6,
          alpha: 0.7 + Math.random() * 0.2,
          gravity: 0.2 + Math.random() * 0.15,
          friction: 0.99,
          rotationSpeed: (Math.random() - 0.5) * 8,
          hard,
        });
      },
    });
  }

  // === PHASE 4b: Gem cascade — 3 expanding ring waves ===
  // Timed rings of gems popping outward like reward ripples
  for (let wave = 0; wave < 3; wave++) {
    const waveDelay = 225 + wave * 135;
    const waveRadius = blastRadius * (0.5 + wave * 0.4);
    const gemsPerWave = 16 + wave * 4; // more gems in outer waves
    core.pendingBlasts.push({
      timestamp: Date.now() + waveDelay,
      callback: () => {
        for (let i = 0; i < gemsPerWave; i++) {
          const ang = (i / gemsPerWave) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
          const cascadeColor = palette[Math.floor(Math.random() * palette.length)];
          core.spawnParticleAtAngle(
            x, y, ang,
            (waveRadius / 0.4) + Math.random() * 4, // speed scales with wave distance
            cascadeColor,
            0.4 + Math.random() * 0.4,
            0.4 + Math.random() * 0.5,
            0.15 + Math.random() * 0.1,
            0.96,
            true, Math.random() < 0.6,
            0.7 + Math.random() * 0.2,
          );
        }
        // Ring shockwave per wave
        core.spawnShockwave(x, y, palette[wave % palette.length], waveRadius, 220, 1.5, 0.2);
      },
    });
  }

  // === PHASE 4c: Gem fountain — upward arc from center ===
  // Gems shoot up and arc outward, fireworks feel
  const fountainCount = 40;
  for (let i = 0; i < fountainCount; i++) {
    const delay = 150 + Math.random() * 225;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const fColor = palette[Math.floor(Math.random() * palette.length)];
        const p = core.getInactive();
        if (!p) return;

        const hard = Math.random() < 0.6;

        configParticle(p, core, {
          x: x + (Math.random() - 0.5) * 20,
          y,
          vx: (Math.random() - 0.5) * 8,  // horizontal spread
          vy: -(8 + Math.random() * 12),   // strong upward
          color: fColor,
          life: 0.6 + Math.random() * 0.8,
          scale: 0.35 + Math.random() * 0.5,
          alpha: 0.8,
          gravity: 0.4 + Math.random() * 0.15, // arcs back down
          friction: 0.99,
          rotationSpeed: (Math.random() - 0.5) * 10,
          hard,
        });
      },
    });
  }

  // === PHASE 4d: Gem confetti — tiny slow-falling celebration ===
  // Lots of tiny gems with heavy drift and fast spin
  const confettiCount = 40;
  for (let i = 0; i < confettiCount; i++) {
    const delay = 260 + Math.random() * 450;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const cColor = palette[Math.floor(Math.random() * palette.length)];
        const p = core.getInactive();
        if (!p) return;

        const ang = Math.random() * Math.PI * 2;
        const dist = Math.random() * blastRadius * 0.9;
        const hard = Math.random() < 0.5;

        configParticle(p, core, {
          x: x + Math.cos(ang) * dist,
          y: y + Math.sin(ang) * dist,
          vx: (Math.random() - 0.5) * 6, // horizontal drift
          vy: (Math.random() - 0.5) * 3,
          color: cColor,
          life: 0.4 + Math.random() * 0.5,
          scale: 0.15 + Math.random() * 0.25, // tiny
          alpha: 0.6 + Math.random() * 0.3,
          gravity: 0.08 + Math.random() * 0.06, // gentle fall
          friction: 0.995,
          rotationSpeed: (Math.random() - 0.5) * 14, // fast tumble
          hard,
        });
      },
    });
  }

  // === PHASE 5: Landing twinkles (delayed ~750-1200ms) ===
  // Final sparkle pops below the explosion — gems hitting the ground
  const landingCount = 14;
  for (let i = 0; i < landingCount; i++) {
    const delay = 750 + Math.random() * 450;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const lx = x + (Math.random() - 0.5) * blastRadius * 1.6;
        const ly = y + blastRadius * (0.3 + Math.random() * 0.5);
        const lColor = Math.random() < 0.25 ? 0xffffff : palette[Math.floor(Math.random() * palette.length)];
        core.spawnParticle(
          lx, ly, lColor,
          0, 0.3,
          0.04, 0.1,
          0.3, 0.6,
          0, 0.5,
          true, Math.random() < 0.5,
          0.9,
        );
      },
    });
  }

  // === SAKURAI: End flash — "sudden disappearance" to punctuate the explosion ===
  // "Around this point, a flash-like effect is incorporated, creating a quick sudden disappearance"
  core.pendingBlasts.push({
    timestamp: Date.now() + 600,
    callback: () => {
      // Brief bright pop with black — same principle as initial flash
      for (let i = 0; i < 10; i++) {
        const r = Math.random();
        const endColor = r < 0.25 ? 0x000000
          : r < 0.5 ? color
            : r < 0.75 ? 0xffffff
              : palette[Math.floor(Math.random() * palette.length)];
        core.spawnParticle(
          x, y, endColor,
          0, 0.8,
          0.06, 0.12,       // very brief
          2.0, 4.0,
          0, 0.5,
          endColor !== 0x000000,  // black = NORMAL
          false,
          endColor === 0x000000 ? 0.6 : 0.8,
        );
      }
      // Snap shockwave — fast, thin, punctuates the end
      core.spawnShockwave(x, y, color, blastRadius * 0.8, 500, 2, 0.1);
    },
  });

  // === SAKURAI: Ground shockwave — horizontal line at ground level ===
  // "In the treatment version, a shockwave appears on the ground"
  // Two impact lines expanding left and right along the ground
  core.pendingBlasts.push({
    timestamp: Date.now() + 90,
    callback: () => {
      const groundLife = 0.25;
      // Left
      core.impactLines.push({
        x, y,
        angle: Math.PI,
        innerRadius: 0,
        outerRadius: blastRadius * 1.2,
        expandSpeed: 250,
        color: Math.random() < 0.5 ? color : 0xffffff,
        lineWidth: 3,
        life: groundLife,
        maxLife: groundLife,
        freezeImmune: false,
      });
      // Right
      core.impactLines.push({
        x, y,
        angle: 0,
        innerRadius: 0,
        outerRadius: blastRadius * 1.2,
        expandSpeed: 250,
        color: Math.random() < 0.5 ? color : 0xffffff,
        lineWidth: 3,
        life: groundLife,
        maxLife: groundLife,
        freezeImmune: false,
      });
    },
  });
}

/** Red hit cross — fighting game impact marker, visible during hitstop freeze */
export function spawnHitCross(core: ParticleCore, x: number, y: number, blastRadius: number): void {
  // Primary cross — thick red X (freeze-immune: animates through hitstop)
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const life = 0.25;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: blastRadius * 0.6,
      expandSpeed: 40,
      color: 0xff2222,
      lineWidth: 5,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }
  // Secondary thinner cross at different angle — layered depth
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2;
    const life = 0.2;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: blastRadius * 0.4,
      expandSpeed: 20,
      color: 0xcc0000,
      lineWidth: 3,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }
  // Brief red flash particles at center (freeze-immune)
  for (let i = 0; i < 6; i++) {
    const redColor = Math.random() < 0.5 ? 0xff3333 : 0xcc0000;
    const p = core.spawnParticle(
      x, y, redColor,
      0, 1,
      0.1, 0.2,
      2.0, 4.0,
      0, 0.5,
      false, false,  // NORMAL — crisp red, not ADD-washed
      0.8,
    );
    // Mark last spawned as freeze-immune
    if (p) p.freezeImmune = true;
  }
}

/**
 * Structural debris — wireframe fragment collapse effect.
 * Cool blue/electric palette matching wireframe material.
 *
 * Layer 1: Chunks (70%) — angular metallic pieces, high gravity, short life
 * Layer 2: Sparks (30%) — fast velocity-stretched cyan streaks, energy discharge
 * Layer 3: Flash shockwave — expanding electric blue ring
 */
export function spawnStructuralDebris(
  core: ParticleCore,
  cx: number, cy: number,
  cellCount: number,
): void {
  const totalParticles = Math.min(
    cellCount * STRUCTURAL.DEBRIS_PER_CELL,
    STRUCTURAL.DEBRIS_CAP,
  );

  const darkColors = [0x0a1a2a, 0x0c1e3a, 0x081828];
  const brightColors = [0x2a4a7a, 0x3060aa, 0x2850aa];
  const accentColors = [0x4488ff, 0x5599ff, 0x88ccff];

  // === Layer 1: Chunks (70%) — heavy metallic pieces ===
  const chunkCount = Math.ceil(totalParticles * 0.7);
  for (let i = 0; i < chunkCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 2 + Math.random() * 5;
    const r = Math.random();
    const color = r < 0.4 ? darkColors[Math.floor(Math.random() * darkColors.length)]
      : r < 0.75 ? brightColors[Math.floor(Math.random() * brightColors.length)]
        : accentColors[Math.floor(Math.random() * accentColors.length)];

    const p = core.getInactive();
    if (!p) return;

    configParticle(p, core, {
      x: cx + (Math.random() - 0.5) * 20,
      y: cy + (Math.random() - 0.5) * 20,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2 - Math.random() * 3,
      color,
      life: 0.2 + Math.random() * 0.3,
      scale: 0.25 + Math.random() * 0.4,
      alpha: 0.8 + Math.random() * 0.2,
      gravity: 0.18 + Math.random() * 0.04,
      friction: 0.97,
      rotationSpeed: (Math.random() - 0.5) * 12,
      addBlend: false,
    });
  }

  // === Layer 2: Sparks (30%) — fast electric streaks ===
  const sparkCount = Math.ceil(totalParticles * 0.3);
  for (let i = 0; i < sparkCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 8 + Math.random() * 10;
    const color = accentColors[Math.floor(Math.random() * accentColors.length)];

    core.spawnParticleAtAngle(
      cx + (Math.random() - 0.5) * 10,
      cy + (Math.random() - 0.5) * 10,
      angle, speed,
      color,
      0.08 + Math.random() * 0.12,
      0.12 + Math.random() * 0.15,
      0.06,
      0.94,
      true, true,
      0.8,
      3 + Math.random() * 2,
    );
  }

  // === Layer 3: Flash shockwave — electric blue ring ===
  core.spawnShockwave(cx, cy, 0x4488ff, 40, 200, 2, 0.18);
}
